import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { getBreakdownPreview, setBreakdown } from '../../truckmateStore';
import { shortDate } from './model';
import { IcWrench } from './cardIcons';
import CustomerEmails from './CustomerEmails';

// 🚨 Breakdown switch on every load. Turning it on emails AND texts every
// customer / broker / receiver on the load: "breakdown, the ETA will be
// impacted, we'll get back to you". Turning it off can send "back on the road".
// The dispatcher sees exactly who gets what before anything goes out.
export default function BreakdownButton({ trip, active, onChanged }) {
  const [p, setP] = useState(null);       // preview
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [done, setDone] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const open = async (e) => {
    e.stopPropagation(); setErr(''); setDone(null); setNote(''); setNotify(true); setBusy(true);
    try { const r = await getBreakdownPreview(trip); setP(r); setSubject(r.subject || ''); setBody(r.text || ''); } catch (ex) { setErr(ex.message || 'Could not load.'); setP({ error: true }); } finally { setBusy(false); }
  };
  const close = () => { setP(null); if (done && onChanged) onChanged(); };
  const go = async () => {
    setBusy(true); setErr('');
    // only what they changed: the rest stays Jarvis's own email
    const edits = { ...(subject.trim() && subject.trim() !== p.subject ? { subject: subject.trim() } : {}), ...(body.trim() && body.trim() !== String(p.text || '').trim() ? { body: note.trim() ? `${body.trim()}\n\n${note.trim()}` : body } : {}) };
    try { setDone(await setBreakdown(trip, { on: p.turningOn, notify, note, ...edits })); } catch (ex) { setErr(ex.message || 'Could not save.'); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" className={`tmw-btn tmw-btn-sm tmw-bd-btn${active ? ' is-on' : ''}`} aria-pressed={!!active} onClick={open} disabled={busy}>
        <IcWrench /> {active ? 'Customers notified' : 'Notify customers'}
      </button>
      {p && createPortal(
        <div className="truckmate-workspace tmw-viewer-root"><div className="tmw-viewer" role="dialog" aria-modal="true" aria-label={`Breakdown for load ${trip}`} onClick={close}>
          <div className="tmw-viewer-box tmw-bd-box" onClick={(e) => e.stopPropagation()}>
            <div className="tmw-viewer-head"><b>{p.turningOn ? `Notify customers: load ${trip}` : `Load ${trip} back on the road`}</b><button type="button" className="tmw-btn tmw-btn-sm" onClick={close}>Close</button></div>
            <div className="tmw-bd-body">
              {err && <div className="tmw-conflict">{err}</div>}
              {!p.error && !done && (
                <>
                  <div className="tmw-conflict">This goes to customers and brokers. For an update to our team, use Breakdown update on the card.</div>
                  <label className="tmw-consent"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} /><span>{p.turningOn ? 'Notify every customer on this load (email + text)' : 'Send a “back on the road” update (email + text)'}</span></label>
                  {notify && (
                    <>
                      <CustomerEmails key={p.emails.join(',')} trip={trip} emails={p.emails} company={p.company} outlook={p.outlook} onSaved={(r) => setP((cur) => ({ ...cur, emails: r.emails }))} />
                      <div className="tmw-small"><b>Text</b> ({p.phones.length}): {p.phones.map((x) => `(${x.slice(0, 3)}) ${x.slice(3, 6)}-${x.slice(6)}`).join(', ') || 'no customer phones on file'}{!p.texting && p.phones.length ? ' · texting not live yet — will not send' : ''}</div>
                      <label className="tmw-email-compose">Email subject<input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={250} /></label>
                      <label className="tmw-email-compose">Email message{body.trim() !== String(p.text || '').trim() ? ' (edited: it goes out as you wrote it)' : ''}<textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} /></label>
                      {body.trim() !== String(p.text || '').trim() && <button type="button" className="tmw-link" onClick={() => setBody(p.text || '')}>Undo my changes to the message</button>}
                      <div className="tmw-label">Text message</div><div className="tmw-small">“{p.sms}”</div>
                      <label className="tmw-email-compose">Add a line (optional){p.turningOn ? ' — e.g. “Road service is on the way.”' : ' — e.g. “Alternator replaced.”'}
                        <textarea rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
                      </label>
                    </>
                  )}
                  <div className="tmw-actions">
                    <button type="button" className={`tmw-btn${p.turningOn ? ' tmw-btn-danger' : ' tmw-btn-primary'}`} onClick={go} disabled={busy}>
                      {busy ? 'Sending…' : p.turningOn ? (notify ? 'Turn on Breakdown & notify customers' : 'Turn on Breakdown (no notice)') : (notify ? 'Turn off & send update' : 'Turn off (no update)')}
                    </button>
                    <button type="button" className="tmw-btn" onClick={close}>Cancel</button>
                  </div>
                  {p.turningOn && <div className="tmw-source">While Breakdown is on, the 3-hour location emails pause so customers don’t get mixed messages.</div>}
                </>
              )}
              {done && (
                <div role="status">
                  <div><b>{done.breakdown.on ? 'Breakdown is ON' : 'Breakdown is off'}</b> · {shortDate(Date.parse(done.breakdown.at))} · {done.breakdown.by}</div>
                  <div className="tmw-small">Email: {done.emailStatus}{done.emailed.length ? ` (${done.emailed.join(', ')})` : ''}</div>
                  <div className="tmw-small">Text: {done.textStatus}</div>
                  {done.errors.length > 0 && <div className="tmw-conflict">{done.errors.join(' · ')}</div>}
                  <div className="tmw-actions"><button type="button" className="tmw-btn tmw-btn-primary" onClick={close}>Done</button></div>
                </div>
              )}
            </div>
          </div>
        </div></div>,
        document.body,
      )}
    </>
  );
}
